import React from 'react';
import ReactModal from 'react-modal';

import { useDrawerStore } from 'context/DrawerContext';
import { useModalDispatch, useModalStore } from 'context/ModalContext';
import { SCROLL_LOCK_IGNORE_CLASS, clearAllBodyScrollLocks, disableBodyScroll } from 'helpers/ScrollHelpers';
import { cx } from 'helpers/StylingHelpers';
import { generateUUID, isNullOrUndefined } from 'helpers/utils';
import { useInView } from 'hooks/common/useInView';
import { useBreakpoint } from 'hooks/useBreakpoint';

import ModalContent from './ModalContent';
import ModalHeader from './ModalHeader';
import ModalOverlay from './ModalOverlay';

import type { Props as ModalHeaderProps } from './ModalHeader';

import { Breakpoints, Positions } from 'types';
import type { Breakpoint, Position } from 'types';

import styles from './Modal.module.scss';

interface BreakpointSpecificProps {
  position?: Position,
  isFullScreen?: boolean,
  withPadding?: boolean,
  withCloseButton?: boolean
}

// TODO
// Refactor Drawer integration

interface Props {
  //
  isOpen: boolean,
  id?: string,
  //
  overlayRef?: (instance: HTMLDivElement) => void,
  bodyRef?: (instance: HTMLDivElement) => void,
  //
  className?: string,
  classNames?: {
    overlay?: string,
    background?: string,
    content?: string,
    inner?: string,
    header?: string,
    title?: string,
    closeButton?: string
  },
  //
  title?: string | React.ReactElement,
  headerProps?: ModalHeaderProps,
  //
  wide?: boolean,
  withPadding?: boolean,
  withCloseButton?: boolean,
  shouldReserveScrollBarGap?: boolean,
  shouldCloseOnEsc?: boolean,
  shouldCloseOnOverlayClick?: boolean,
  shouldFocusAfterRender?: boolean,
  shouldReturnFocusAfterClose?: boolean,
  //
  breakpoint?: Breakpoint,
  desktopProps?: BreakpointSpecificProps,
  responsiveProps?: BreakpointSpecificProps,
  //
  onClose?: () => void,
  onAfterClose?: () => void,
  onAfterOpen?: () => void,
  onOverlayOpen?: () => void,
  onOverlayClose?: () => void,
  onContentOpening?: () => void,
  onContentOpen?: () => void,
  onContentClosing?: () => void,
  onContentClose?: () => void,
  //
  dataTestId?: string,
  children?: React.ReactNode
  //
}

const Modal = (props: Props) => {
  const {
    //
    isOpen,
    id,

    // Refs
    overlayRef: onInitOverlayRef = () => {},
    bodyRef: onInitBodyRef = () => {},

    // Classes
    className,
    classNames = {},

    // Header
    title = '',
    headerProps,

    // Flags
    wide = false,
    withCloseButton = true,
    withPadding = true,
    shouldReserveScrollBarGap = true,
    shouldCloseOnEsc = true,
    shouldCloseOnOverlayClick = true,
    shouldFocusAfterRender = true,
    shouldReturnFocusAfterClose = false,

    // Breakpoint
    breakpoint = Breakpoints.MD,
    desktopProps = {
      position: Positions.CENTER
    },
    responsiveProps = {
      position: Positions.BOTTOM
    },

    // Handlers
    onClose = () => {},
    onAfterClose = () => {},
    onAfterOpen = () => {},
    onOverlayOpen = () => {},
    onOverlayClose = () => {},
    onContentOpening = () => {},
    onContentOpen = () => {},
    onContentClosing = () => {},
    onContentClose = () => {},

    // Misc
    children,
    dataTestId,
    ...rest

  } = props;

  // Refs

  const idRef = React.useRef(id || generateUUID());
  const depthRef = React.useRef<number | undefined>(undefined);

  const overlayRef = React.useRef<HTMLDivElement>();
  const contentRef = React.useRef<HTMLDivElement>();
  const innerRef = React.useRef<HTMLDivElement>(null);

  // Hooks

  const isBreakpoint = useBreakpoint(breakpoint, 'down');
  const { observe: anchorRef, inView: isNotScrolled } = useInView();

  // --- State Hooks

  const [isOverlayOpen, setIsOverlayOpen] = React.useState(false);
  const [isContentOpen, setIsContentOpen] = React.useState(false);

  // --- Context Hooks

  const { visible: isDrawerOpen } = useDrawerStore();

  const { stack: modalStack } = useModalStore();
  const { addModalToStack, removeModalFromStack } = useModalDispatch();

  // Props

  const shouldRegisterOnStack = React.useMemo(() => !modalStack.contains(idRef.current), [modalStack]);
  const stackEntry = modalStack.getById(idRef.current);
  const { depth } = stackEntry || {};

  // Handlers

  const handleAfterOpen = () => {
    setIsOverlayOpen(true);
    setIsContentOpen(true);
    onAfterOpen();
    if (!isDrawerOpen && innerRef?.current) {
      disableBodyScroll(
        innerRef?.current,
        { reserveScrollBarGap: !isBreakpoint && shouldReserveScrollBarGap }
      );
    }
  };

  const handleAfterClose = () => {
    onAfterClose?.();
    if (!isDrawerOpen && modalStack.isEmpty()) {
      clearAllBodyScrollLocks();
    }
  };

  // Effects

  React.useEffect(() => {
    if (!isOpen) {
      removeModalFromStack(idRef.current);
      setIsOverlayOpen(false);
      setIsContentOpen(false);
      return;
    }
    if (shouldRegisterOnStack) {
      addModalToStack({ id: idRef.current });
    }
  }, [shouldRegisterOnStack, isOpen]);

  // --- Overlay Ref

  React.useEffect(() => {
    if (overlayRef.current) {
      onInitOverlayRef(overlayRef.current);
    }
  }, [overlayRef.current,]);

  // --- Body Ref

  React.useEffect(() => {
    if (innerRef.current) {
      onInitBodyRef(innerRef.current);
    }
  }, [innerRef.current]);

  // --- Stack Depth

  React.useEffect(() => {
    if (isNullOrUndefined(depth) || depthRef.current !== undefined) {
      return;
    }
    depthRef.current = (depth as number);
  }, [depth]);

  // --- Remove from stack on unmount

  React.useEffect(() => {
    return () => {
      removeModalFromStack(idRef.current);
    };
  }, []);

  // Props

  const defaultPosition = isBreakpoint
    ? Positions.CENTER
    : Positions.BOTTOM;

  const {
    position = defaultPosition,
    isFullScreen,
    withPadding: withPaddingAtBreakpoint = withPadding,
    withCloseButton: withCloseButtonAtBreakpoint = withCloseButton,
  } = (isBreakpoint ? responsiveProps : desktopProps) || {};

  const {
    desktopProps: headerDesktopProps = {},
    responsiveProps: headerResponsiveProps = {},
    ...headerRestProps
  } = headerProps || {};

  // --- Flags

  const hasHeader = (title || (withCloseButton && withCloseButtonAtBreakpoint));

  // Render

  return (
    <ReactModal
      isOpen={isOpen}
      // animation
      closeTimeoutMS={250}
      // classes
      overlayClassName={cx(
        styles.overlay,
        className,
      )}
      className={cx(
        styles.content,
        { [styles.desktop]: !isBreakpoint },
        { [styles.responsive]: isBreakpoint },
        { [styles.fullScreen]: isFullScreen },
        SCROLL_LOCK_IGNORE_CLASS,
        classNames?.content
      )}
      // refs
      overlayRef={(node: HTMLDivElement) => overlayRef.current = node}
      contentRef={(node: HTMLDivElement) => contentRef.current = node}
      // renderers
      overlayElement={(
        overlayProps: React.ComponentPropsWithRef<'div'>,
        contentElement: React.ReactElement
      ) => {
        return (
          <ModalOverlay
            isOpen={isOverlayOpen}
            //
            classNames={{
              background: cx(
                classNames?.background
              )
            }}
            //
            position={position}
            isResponsive={isBreakpoint}
            isFullScreen={!!isFullScreen}
            depth={depthRef.current}
            //
            onEntered={onOverlayOpen}
            onExited={onOverlayClose}
            //
            dataTestId={dataTestId}
            //
            {...overlayProps}
            //
            className={cx(
              classNames?.overlay,
            )}
          >
            {contentElement}
          </ModalOverlay>
        );
      }}
      contentElement={(
        contentProps: React.ComponentPropsWithRef<'div'>,
        contentChildren: React.ReactNode
      ) => {
        return (
          <ModalContent
            isOpen={isContentOpen}
            //
            position={position}
            isResponsive={isBreakpoint}
            isFullScreen={!!isFullScreen}
            isWide={wide}
            depth={depthRef.current}
            //
            onEntering={onContentOpening}
            onEntered={onContentOpen}
            onExiting={onContentClosing}
            onExited={onContentClose}
            //
            dataTestId={dataTestId}
            //
            {...contentProps}
          >
            {contentChildren}
          </ModalContent>
        );
      }}
      // flags
      preventScroll
      ariaHideApp={false}
      //
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
      shouldFocusAfterRender={shouldFocusAfterRender}
      shouldCloseOnEsc={shouldCloseOnEsc}
      // handlers
      onRequestClose={onClose}
      onAfterOpen={handleAfterOpen}
      onAfterClose={handleAfterClose}
      // test
      data={{ testid: dataTestId }}
      // rest
      {...rest}
    >

      {/* Header */}
      {
        !hasHeader
          ? null
          : (
            <ModalHeader
              className={classNames.header}
              classNames={{
                title: classNames.title,
                closeButton: classNames.closeButton
              }}
              //
              title={title}
              //
              breakpoint={breakpoint}
              withPadding
              desktopProps={headerDesktopProps}
              responsiveProps={headerResponsiveProps}
              //
              stuck={!isNotScrolled}
              dataTestId={dataTestId}
              //
              onClose={onClose}
              //
              {...headerRestProps}
            />
          )
      }

      {/* Inner */}
      <div
        ref={innerRef}
        className={cx(
          styles.inner,
          { [styles.hasTitle]: !!title },
          { [styles.padded]: withPadding && withPaddingAtBreakpoint },
          SCROLL_LOCK_IGNORE_CLASS,
          classNames?.inner,
        )}
      >

        {/* Anchor */}
        <div ref={anchorRef} className={styles.anchor} />

        {/* Body */}
        {children}

      </div>

    </ReactModal>
  );
};

export default React.memo(Modal);
